import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Calendar, FolderKanban, Users, Palmtree,
  FileText, Receipt, Building2, LogOut, Menu, ChevronLeft, Settings, Package, Layers, BookText, Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, module: 'dashboard' },
  { path: '/einsatzplanung', label: 'Einsatzplanung', icon: Calendar, module: 'einsatzplanung' },
  { path: '/projekte', label: 'Projekte', icon: FolderKanban, module: 'projekte' },
  { path: '/mitarbeiter', label: 'Mitarbeiter', icon: Users, module: 'mitarbeiter' },
  { path: '/urlaub', label: 'Abwesenheit', icon: Palmtree, module: 'abwesenheiten' },
  { path: '/dokumente', label: 'Dokumente', icon: FileText, module: 'dokumente' },
  { path: '/finanzen', label: 'Finanzen', icon: Receipt, module: 'finanzen' },
  { path: '/rechnungsposten', label: 'Posten', icon: BookText, module: 'rechnungsposten' },
  { path: '/lager', label: 'Lager', icon: Package, module: 'lager' },
  { path: '/materialien', label: 'Materialien', icon: Layers, module: 'materialien' },
  { path: '/kunden', label: 'Kunden', icon: Building2, module: 'kunden' },
  { path: '/einstellungen', label: 'Einstellungen', icon: Settings, module: 'einstellungen' },
];

export default function Layout({ children }) {
  const { user, logout, API, canRead, tenant } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const planColors = { trial: 'text-amber-400 bg-amber-400/10', starter: 'text-slate-400 bg-slate-400/10', pro: 'text-amber-400 bg-amber-400/10', enterprise: 'text-violet-400 bg-violet-400/10' };

  return (
    <div className="flex h-screen bg-slate-950" data-testid="app-layout">
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside data-testid="sidebar" className={`
        fixed lg:static inset-y-0 left-0 z-50
        ${collapsed ? 'w-16' : 'w-64'}
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        bg-slate-900 border-r border-slate-800 flex flex-col transition-all duration-200
      `}>
        <div className="flex items-center justify-between p-4 h-16">
          {!collapsed && (
            <div>
              <span className="text-lg font-black text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Mont<span className="text-amber-500">ARO</span></span>
              {tenant?.plan && <Badge className={`${planColors[tenant.plan] || ''} rounded-sm text-[9px] ml-2 capitalize`}>{tenant.plan}</Badge>}
            </div>
          )}
          <Button variant="ghost" size="icon" onClick={() => { setCollapsed(!collapsed); setMobileOpen(false); }}
            className="text-slate-400 hover:text-white hover:bg-slate-800" data-testid="sidebar-toggle">
            {collapsed ? <Menu className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </Button>
        </div>
        <Separator className="bg-slate-800" />
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {navItems.filter(item => canRead(item.module)).map(item => {
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-200
                  ${active
                    ? 'bg-amber-500 text-slate-950 font-semibold shadow-[0_0_15px_rgba(245,158,11,0.3)]'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}>
                <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
                {!collapsed && <span className="text-sm">{item.label}</span>}
              </Link>
            );
          })}
          {user?.role === 'superadmin' && (
            <Link to="/superadmin" onClick={() => setMobileOpen(false)}
              data-testid="nav-superadmin"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-200 mt-2 border-t border-slate-800/40 pt-3
                ${location.pathname === '/superadmin'
                  ? 'bg-violet-500 text-white font-semibold'
                  : 'text-violet-400 hover:text-white hover:bg-violet-500/10'
                }`}>
              <Crown className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              {!collapsed && <span className="text-sm">Super Admin</span>}
            </Link>
          )}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3">
            <Avatar className="w-8 h-8">
              <AvatarFallback className="bg-amber-500 text-slate-950 font-bold text-xs">
                {user?.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            )}
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button"
              className="text-slate-400 hover:text-rose-400 hover:bg-slate-800 shrink-0">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800 flex items-center px-4 sm:px-6 justify-between shrink-0">
          <Button variant="ghost" size="icon" className="lg:hidden text-slate-400" onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-toggle">
            <Menu className="w-5 h-5" />
          </Button>
          <span className="text-sm font-medium text-slate-300">
            {navItems.find(i => location.pathname === i.path)?.label || 'MontARO'}
          </span>
          <span className="text-xs text-slate-500 font-mono">{new Date().toLocaleDateString('de-DE')}</span>
        </header>
        {/* Trial Banner */}
        {tenant?.plan === 'trial' && (
          <div className={`px-4 py-2 text-center text-xs font-medium ${tenant.trial_expired
            ? 'bg-rose-500/10 text-rose-400 border-b border-rose-500/20'
            : 'bg-amber-500/10 text-amber-400 border-b border-amber-500/20'}`}>
            {tenant.trial_expired
              ? <>Ihre Testphase ist abgelaufen. <a href="/einstellungen" className="underline font-bold">Jetzt Paket wählen</a> um MontARO weiter nutzen zu können.</>
              : <>Testphase: noch {tenant.trial_days_left} Tage verbleibend · <a href="/einstellungen" className="underline">Paket wählen</a></>
            }
          </div>
        )}
        <div className="flex-1 overflow-auto p-4 sm:p-6">{children}</div>
      </main>
    </div>
  );
}
